import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './CampaignItem.less';
import { ReactComponent as TimelineSvg } from '@assets/icons/timeline.svg';
import { ReactComponent as PencilSvg } from '@assets/icons/pencil-write.svg';
import { ReactComponent as SortArrowSvg } from '@assets/icons/sort-arrow.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as SortListSvg } from '@assets/icons/sort-list.svg';
import { ReactComponent as SortTilesSvg } from '@assets/icons/sort-tiles.svg';
import { Input, Tabs, Dropdown, Menu, Button, message } from 'antd';
import CampaignTimeline from '@components/CampaignTimeline';
import CampaignDropDownMenu from '@components/EntitiesDropdownMenu/CampaignDropDownMenu';
import { getMembersList, getMembersCount } from '@api/Campaign';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useTypedSelector } from '@hooks';
import {
  toggleApprovalConflictAssetsModal,
  toggleCampaignInfoModal
} from '@redux/actions/modalAction';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import { useLayout } from '@layouts/PageLayout';
import { useOrganization } from '@components/OrganizationBoundary';
import AssetsTab from '@pages/Campaigns/CampaignItem/components/AssetsTab/AssetsTab';
import TasksTab from '@pages/Campaigns/CampaignItem/components/TasksTab/TasksTab';
import classNames from 'classnames';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { nonNull } from '@helpers/non-null';
import { campaignStateSelector as tasksCampaignStateSelector } from '@redux/selectors/tasks';
import {
  fetchCampaignTaskBoards,
  fetchTaskCustomFields
} from '@redux/actions/tasks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useFetch } from '@hooks/useFetch';
import { debounce } from '@helpers/debounce';
import { TasksOrderBy } from '@redux/types/tasks';
import {
  setListParamsInCampaign,
  setListsNeedRefreshInCampaign,
  setScrollTopInCampaign,
  setTaskBoardOpenedInCampaign
} from '@redux/reducers/tasks';
import TasksListControls from '@pages/Campaigns/CampaignItem/components/TasksListControls/TasksListControls';
import AvatarsGroup from '@components/AvatarsGroup/AvatarsGroup';
import ApprovalsTab from '@pages/Campaigns/CampaignItem/components/ApprovalsTab/ApprovalsTab';
import { ReactComponent as ApprovalRoutingSvg } from '@assets/icons/approval-routing.svg';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useGetCountApprovalWorkflows,
  useGetListApprovalWorkflows
} from '@hooks/queries/approval';
import { useDebounced } from '@helpers/useDebounced';
import {
  ApprovalWorkflowControllerCountApprovalWorkflowsParams,
  ApprovalWorkflowControllerListApprovalWorkflowsParams,
  CampaignItemDto
} from '@api/Api';
import { useCampaignQuery } from '@hooks/queries/campaigns';
import { isAxiosError } from 'axios';
import { useAssetsCountQuery } from '@hooks/queries/assets';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import SharedLinksTab from '@pages/Campaigns/CampaignItem/components/SharedLinksTab/SharedLinksTab';
import {
  useAssetReviewCountQuery,
  useListAssetReviewsQuery
} from '@hooks/queries/assetReview';

const { TabPane } = Tabs;

type copyForSearchProps = {
  [id: string]: { copy: string; width: number };
};

function Operations({
  activeTabKey,
  onAssetSearch,
  isTileView,
  setIsTileView,
  initOrderBy,
  onOrderBy
}: {
  activeTabKey: string;
  onAssetSearch: (a: string | undefined) => Promise<void>;
  isTileView: boolean;
  setIsTileView: (a: boolean) => void;
  initOrderBy?: string;
  onOrderBy: (a: any) => void;
}) {
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [selectedSorter, setSelectedSorter] = useState<string | undefined>(
    initOrderBy
  );

  const copyForSearch: copyForSearchProps = {
    assets: { copy: 'media or folder', width: 230 },
    approvals: { copy: 'approvals', width: 150 },
    sharedLinks: { copy: 'links', width: 150 }
  };
  const showViewToggle = activeTabKey === 'assets';
  const showSortMenu = activeTabKey !== 'approvals';
  const showSearch = activeTabKey !== 'approvals';
  const { copy, width } = copyForSearch?.[activeTabKey] || {
    copy: '',
    width: 150
  };

  if (!showSearch && !showViewToggle && !showSortMenu) return null;

  return (
    <div className="actions-container" id="operations-container">
      {showSearch && (
        <Input
          style={{
            width: '100%',
            maxWidth: width,
            marginRight: 16,
            padding: '8px 12px'
          }}
          placeholder={`Search ${copy}`}
          prefix={<SearchSvg />}
          size="large"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (activeTabKey === 'assets') {
              onAssetSearch(e.target.value || undefined);
            }
          }}
        />
      )}
      {showSortMenu && (
        <Dropdown
          getPopupContainer={() =>
            document.querySelector('#operations-container') as HTMLElement
          }
          overlay={
            <Menu
              selectedKeys={[selectedSorter as string]}
              onClick={({ key }) => {
                setSelectedSorter(key);
                onOrderBy(key);
                onAssetSearch(searchQuery || undefined);
              }}
            >
              <span className="help_dropdown__title">Sort by</span>
              <Menu.Item key="order:DESC">
                <span className="help_dropdown__dot" /> Custom
              </Menu.Item>
              <Menu.Item key="createdAt:DESC">
                <span className="help_dropdown__dot" /> Date created
              </Menu.Item>
              {activeTabKey === 'assets' && (
                <Menu.Item key="modifiedAt:DESC">
                  <span className="help_dropdown__dot" /> Date modified
                </Menu.Item>
              )}
            </Menu>
          }
          placement="bottomLeft"
          overlayClassName="help_dropdown"
          trigger={['click']}
        >
          <Button
            style={{ marginRight: 20, height: 'auto' }}
            type="text"
            icon={<SortArrowSvg />}
          >
            Sort
          </Button>
        </Dropdown>
      )}

      {showViewToggle && (
        <>
          <Button
            type="text"
            style={{ padding: 0, height: 'auto' }}
            className="select-assets-view-btn"
            onClick={() => {
              setIsTileView(true);
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
            }}
          >
            <SortTilesSvg
              style={{ marginRight: 0 }}
              className={isTileView ? 'select-assets-view-icon' : undefined}
            />
          </Button>
          <div className="line" />
          <Button
            type="text"
            className="select-assets-view-btn"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => {
              setIsTileView(false);
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
            }}
          >
            <SortListSvg
              style={{ marginRight: 0 }}
              className={!isTileView ? 'select-assets-view-icon' : undefined}
            />
          </Button>
        </>
      )}
    </div>
  );
}

const draggableAssetsOrder = 'order:DESC';
const defaultAssetsOrder = draggableAssetsOrder;
const assetsOrderByKey = 'assets-order-by';

function CampaignItemInWorkspace({ campaign }: { campaign: CampaignItemDto }) {
  const queryClient = useQueryClient();
  const { tourData } = useOnboardingProcess();
  const { currentView, setCurrentView } = useLayout();
  const [workspace] = useCurrentWorkspace();
  const { currentOrganization } = useOrganization(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const tabKeyParam = searchParams.get('tab') ?? 'assets';
  const dispatch = useTypedDispatch();
  const [openTimeline, setOpenTimeline] = useState<boolean>(false);
  const [activeTabKey, _setActiveTabKey] = useState<string>(tabKeyParam);
  const statusesParam = searchParams.getAll('statuses');
  const dueDateParam = searchParams.getAll('dueDate');
  const createdDateParam = searchParams.getAll('createdDate');
  const managersParam = searchParams.getAll('managers');
  const sortByParam = (searchParams.get('sortBy') ||
    'needReview') as ApprovalWorkflowControllerListApprovalWorkflowsParams['orderBy'];
  const searchParam = searchParams.get('search') || undefined;
  const searchTextParam = useDebounced(searchParam, 600);

  const canUseBatchApprovals =
    !!currentOrganization?.entity?.features.batchApprovals;
  const awFeatureEnabled =
    !!currentOrganization?.entity?.features.approvalRouting;

  const paramsRef = useRef(params);
  paramsRef.current = params;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const setActiveTabKey = useCallback(
    (key: string) => {
      if (searchParamsRef.current.get('tab') !== key) {
        const newParams = new URLSearchParams();
        newParams.set('tab', key);
        searchParamsRef.current.set('tab', key);
        setSearchParams(newParams);
      }
      _setActiveTabKey(key);
    },
    [_setActiveTabKey, setSearchParams]
  );
  if (tabKeyParam !== activeTabKey) {
    _setActiveTabKey(tabKeyParam);
  }

  const [orderBy, setOrderBy] = useState<string | undefined>(() => {
    if (tabKeyParam === 'assets') {
      return localStorage.getItem(assetsOrderByKey) || defaultAssetsOrder;
    }
    return undefined;
  });
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');

  const approvalWorkflowsQueryParams = useMemo(() => {
    return {
      searchQuery: searchTextParam,
      dueDateFrom: dueDateParam?.[1] ? dueDateParam[0] : undefined,
      dueDateTo: dueDateParam?.[1] ? dueDateParam[1] : undefined,
      createdAtFrom: createdDateParam?.[1] ? createdDateParam[0] : undefined,
      createdAtTo: createdDateParam?.[1] ? createdDateParam[1] : undefined,
      managers: managersParam.length ? managersParam : undefined,
      statuses: statusesParam.length
        ? (statusesParam as ApprovalWorkflowControllerCountApprovalWorkflowsParams['statuses'])
        : undefined
    };
  }, [
    managersParam,
    statusesParam,
    dueDateParam,
    createdDateParam,
    searchTextParam
  ]);

  const prevCampaignId = useRef(campaign.id);

  const { data: approvalsCount, refetch: refetchApprovalsCount } =
    useGetCountApprovalWorkflows({
      keepPreviousData: campaign.id === prevCampaignId.current,
      params: {
        workspaceId: workspace.id,
        campaignId: [campaign.id],
        ...approvalWorkflowsQueryParams
      }
    });

  const { data: assetReviewsCount } = useAssetReviewCountQuery({
    keepPreviousData: campaign.id === prevCampaignId.current,
    params: {
      campaignId: campaign.id
    }
  });

  const {
    data: approvals,
    isLoading: approvalsLoading,
    isFetchingNextPage: isFetchingNextPageApprovals,
    hasNextPage: hasNextPageApprovals,
    refetch: refetchApprovals,
    fetchNextPage: fetchNextPageApprovals
  } = useGetListApprovalWorkflows({
    enabled: activeTabKey === 'approvals',
    keepPreviousData: campaign.id === prevCampaignId.current,
    params: {
      workspaceId: workspace.id,
      campaignId: [campaign.id],
      orderBy: sortByParam,
      ...approvalWorkflowsQueryParams
    }
  });

  const {
    data: assetReviewsData,
    isLoading: assetReviewsLoading,
    isFetchingNextPage: isFetchingNextPageAssetReviews,
    hasNextPage: hasNextPageAssetReviews,
    fetchNextPage: fetchNextPageAssetReviews
  } = useListAssetReviewsQuery({
    params: {
      campaignId: campaign.id
    },
    enabled: activeTabKey === 'sharedLinks'
  });

  const assetsCountQuery = useAssetsCountQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten: true,
      isFolder: false
    },
    {
      keepPreviousData: campaign.id === prevCampaignId.current,
      placeholderData: campaign.totalAssetsCount
    }
  );
  const assetsCount = assetsCountQuery.data ?? 0;

  if (campaign.id !== prevCampaignId.current) {
    prevCampaignId.current = campaign.id;
  }

  const approvalsList = useMemo(
    () => approvals?.pages.flatMap((x) => x.edges.map((x) => x.node)) ?? [],
    [approvals]
  );

  const refetchApprovalsData = () => {
    refetchApprovals();
    refetchApprovalsCount();
  };

  const membersInPrivateCampaignQuery = useQuery({
    queryKey: ['campaign', 'members', 'list', 'private', campaign?.id],
    keepPreviousData: true,
    enabled: !!campaign?.isPrivate,
    queryFn: async ({ signal }) => {
      const { data } = await getMembersList(
        {
          campaignId: nonNull(campaign?.id),
          onlyThatHaveAccessToPrivate: true
        },
        { signal }
      );
      return data;
    }
  });
  const membersInPrivateCampaignCountQuery = useQuery({
    queryKey: ['campaign', 'members', 'count', 'private', campaign?.id],
    keepPreviousData: true,
    enabled: !!campaign?.isPrivate,
    queryFn: async ({ signal }) => {
      const { data } = await getMembersCount(
        {
          campaignId: nonNull(campaign?.id),
          onlyThatHaveAccessToPrivate: true
        },
        { signal }
      );
      return data.count;
    }
  });
  const membersInPrivateCampaign = useMemo(
    () => membersInPrivateCampaignQuery.data?.edges.map((x) => x.node) ?? [],
    [membersInPrivateCampaignQuery.data]
  );
  const membersInPrivateCampaignCount =
    membersInPrivateCampaignCountQuery.data ?? 0;

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe && msg.payload.reason !== 'upload') return false;
      return true;
    },
    'asset-created'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    async () => {
      refetchApprovalsData();
    },
    1000,
    (msg) => {
      if (!campaign || msg.payload.campaignId !== campaign.id) return false;
      if (msg.payload.reason === 'approval-workflow-changed') return true;
      if (msg.causedByMe) return false;
      return false;
    },
    'asset-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe && msg.payload.reason !== 'upload') return false;
      return true;
    },
    'asset-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (!campaign || msg.payload.campaignId !== campaign.id) return false;
      if (msg.payload.folderId !== null) return false;
      if (msg.causedByMe && msg.payload.reason !== 'conversion-status-changed')
        return false;
      return true;
    },
    'asset-updated'
  );

  useWebSocketSubscription(async (msg) => {
    if (!campaign || msg.payload.campaignId !== campaign.id) return;
    if (msg.causedByMe) return;
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  }, 'campaign-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (!campaign || msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (!campaign || msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-access-changed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe && msg.payload.reason !== 'copied') return false;
      return true;
    },
    'folder-created'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'folder-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (!campaign || msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'folder-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'folder-access-changed'
  );

  // TASKS
  const workspaceId = workspace.id;
  const campaignId = campaign.id;
  const campaignIdRef = useRef(campaignId);
  campaignIdRef.current = campaignId;
  const tasksCount = useTypedSelector(
    (state) => tasksCampaignStateSelector(state, { campaignId }).count
  );

  useEffect(() => {
    dispatch(setListsNeedRefreshInCampaign({ campaignId }));
  }, [campaignId, dispatch]);

  const prevActiveTabKey = useRef(activeTabKey);
  useEffect(() => {
    if (activeTabKey !== prevActiveTabKey.current) {
      prevActiveTabKey.current = activeTabKey;
      if (activeTabKey === 'tasks') {
        dispatch(
          setListsNeedRefreshInCampaign({ campaignId: campaignIdRef.current })
        );
      }
    }
  }, [activeTabKey, dispatch]);

  const canRestoreTasksScroll = useTypedSelector(
    (state) =>
      activeTabKey === 'tasks' &&
      tasksCampaignStateSelector(state, { campaignId }).boards.fetch
        .initiallyLoaded
  );
  const tasksScrollTop = useTypedSelector(
    (state) => tasksCampaignStateSelector(state, { campaignId }).scrollTop
  );
  const initiallyLoaded = useTypedSelector(
    (state) =>
      tasksCampaignStateSelector(state, { campaignId }).boards.fetch
        .initiallyLoaded
  );
  const tasksScrollTopRef = useRef(tasksScrollTop);
  tasksScrollTopRef.current = tasksScrollTop;
  const tasksScrollContainer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!canRestoreTasksScroll) return;
    const boardId = searchParamsRef.current.get('boardId');
    if (boardId) return;
    requestAnimationFrame(() => {
      if (!tasksScrollContainer.current) return;
      tasksScrollContainer.current.scrollTop = tasksScrollTopRef.current;
    });
  }, [workspaceId, campaignId, canRestoreTasksScroll, dispatch]);
  useEffect(() => {
    if (paramsRef.current.campaignId !== campaignId) return;
    if (!initiallyLoaded) return;
    const boardId = searchParamsRef.current.get('boardId');

    if (boardId) {
      dispatch(
        setTaskBoardOpenedInCampaign({
          value: true,
          campaignId,
          boardId
        })
      );
      setTimeout(() => {
        document
          .querySelector(`[data-board-key="${boardId}"]`)
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
      }, 200);
    }

    searchParamsRef.current.delete('boardId');
    setSearchParams(searchParamsRef.current, { replace: true });
  }, [initiallyLoaded, campaignId, dispatch, setSearchParams]);

  useFetch({
    key: `campaign-custom-fields-${campaignId}`,
    selector: (state) =>
      tasksCampaignStateSelector(state, { campaignId }).customFields.fetch,
    fetch: (fetchType) => {
      return dispatch(fetchTaskCustomFields({ campaignId, fetchType }));
    }
  });
  const boardsLoading = useFetch({
    key: `campaign-task-board-${campaignId}`,
    selector: (state) =>
      tasksCampaignStateSelector(state, { campaignId }).boards.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchCampaignTaskBoards({
          campaignId,
          fetchType
        })
      );
    }
  });
  const onTasksOrderByChange = useCallback(
    (value: TasksOrderBy) => {
      dispatch(
        setListParamsInCampaign({
          orderBy: value,
          campaignId: campaignIdRef.current
        })
      );
      if (tasksScrollContainer.current) {
        tasksScrollContainer.current.scrollTop = 0;
      }
    },
    [dispatch]
  );
  const onTasksSearchQueryChange = useCallback(
    (value: string) => {
      dispatch(
        setListParamsInCampaign({
          searchQuery: value,
          campaignId: campaignIdRef.current
        })
      );
      if (tasksScrollContainer.current) {
        tasksScrollContainer.current.scrollTop = 0;
      }
    },
    [dispatch]
  );
  const onScroll = useMemo(
    () =>
      debounce((e: React.SyntheticEvent<HTMLDivElement>) => {
        const { scrollTop } = e.target as HTMLDivElement;
        tasksScrollTopRef.current = scrollTop;
        dispatch(
          setScrollTopInCampaign({
            value: scrollTop,
            campaignId: campaignIdRef.current
          })
        );
      }, 500),
    [dispatch]
  );
  // TASKS

  return (
    <>
      <div>
        <div className="header-container">
          <div className="title-container" id="CAMPAIGN_NAME">
            <span>{campaign?.name}</span>
            <CampaignDropDownMenu
              campaign={campaign}
              onDeleteCallback={() => navigate('../')}
              placement="bottomLeft"
              overlayClassName="campaign_menu_overlay"
              triggerClassName="header-container__dropdown-trigger"
            />
          </div>
          <div>
            {campaign?.isPrivate && (
              <Button
                type="text"
                className="campaign-users"
                onClick={() =>
                  dispatch(
                    toggleCampaignInfoModal({
                      campaign,
                      visible: true,
                      activeTab: 'members'
                    })
                  )
                }
              >
                <AvatarsGroup
                  membersType="campaign_members"
                  maxCount={3}
                  users={membersInPrivateCampaign}
                  totalCount={membersInPrivateCampaignCount}
                  avatarSize={32}
                />
              </Button>
            )}

            <Button
              className="timeline-button"
              type="default"
              size="small"
              onClick={() => setOpenTimeline(true)}
            >
              <TimelineSvg />
              <span>View timeline</span>
            </Button>
            <Button
              type="default"
              size="small"
              id="CAMPAIGN_INFO_BUTTON"
              className="timeline-button"
              onClick={() =>
                dispatch(
                  toggleCampaignInfoModal({
                    activeTab: 'details',
                    campaign,
                    visible: true
                  })
                )
              }
            >
              <PencilSvg />
              <span>Project info</span>
            </Button>
            {activeTabKey === 'assets' && (
              <UpgradePlanTooltip
                text={
                  !canUseBatchApprovals || !awFeatureEnabled
                    ? ' to an Enterprise plan to route multiple media assets for approval at one time.'
                    : 'Click to route all media in project and folders for approval'
                }
                isCanVisible={true}
                placement="topRight"
                isShowUpgradeText={!canUseBatchApprovals || !awFeatureEnabled}
                innerStyle={{
                  maxWidth: '300px'
                }}
              >
                <>
                  <Button
                    type="default"
                    size="small"
                    className={classNames(
                      'info-button tour-route-for-approval-btn',
                      {
                        'b-has-active-tour':
                          tourData?.type === 'route-for-approval'
                      }
                    )}
                    disabled={!canUseBatchApprovals || !awFeatureEnabled}
                    onClick={() => {
                      if (!canUseBatchApprovals || !awFeatureEnabled) return;
                      dispatch(
                        toggleApprovalConflictAssetsModal({
                          visible: true,
                          title: 'Route project for approval',
                          campaignId: campaign.id,
                          workspaceId: campaign.workspaceId,
                          callback: (aw) => {
                            if (aw) {
                              navigate(
                                `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                              );
                              return;
                            }
                            queryClient.invalidateQueries({
                              queryKey: ['assets', 'list']
                            });
                          }
                        })
                      );
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        gap: '4px',
                        alignItems: 'center'
                      }}
                    >
                      <ApprovalRoutingSvg className="info-button-icon" />
                      <span>Route project for approval</span>
                      {(!canUseBatchApprovals || !awFeatureEnabled) && (
                        <UpgradePlanSvg className="upgrade-icon" />
                      )}
                    </span>
                  </Button>
                </>
              </UpgradePlanTooltip>
            )}
          </div>
        </div>
        <div className="menu-panel-container">
          <Tabs
            activeKey={activeTabKey}
            onChange={(key) => {
              setActiveTabKey(key);
            }}
            tabBarExtraContent={
              <>
                {activeTabKey !== 'tasks' && (
                  <Operations
                    activeTabKey={activeTabKey}
                    onAssetSearch={(v): any => {
                      if (!campaign) return;
                      setSearchQuery(v);
                    }}
                    isTileView={currentView === 'tile'}
                    setIsTileView={(v) => setCurrentView(v ? 'tile' : 'list')}
                    initOrderBy={orderBy}
                    onOrderBy={(orderBy) => {
                      setOrderBy(orderBy);
                      if (activeTabKey === 'assets') {
                        localStorage.setItem(assetsOrderByKey, orderBy);
                      }
                    }}
                  />
                )}
                {activeTabKey === 'tasks' && (
                  <TasksListControls
                    campaignId={campaign.id}
                    onSearchQueryChange={onTasksSearchQueryChange}
                    onOrderByChange={onTasksOrderByChange}
                  />
                )}
              </>
            }
          >
            <TabPane
              tab={
                <>
                  Media{' '}
                  <span
                    className={classNames(
                      'count',
                      assetsCount > 9 && 'count_big'
                    )}
                  >
                    {assetsCount}
                  </span>{' '}
                </>
              }
              key="assets"
            >
              <AssetsTab
                campaign={campaign}
                orderBy={orderBy}
                searchQuery={searchQuery}
                activeTabKey={activeTabKey}
              />
            </TabPane>
            <TabPane
              tab={
                <>
                  Approvals{' '}
                  <span
                    className={classNames(
                      'count',
                      (approvalsCount || 0) > 9 && 'count_big'
                    )}
                  >
                    {approvalsCount || 0}
                  </span>{' '}
                </>
              }
              key="approvals"
            >
              <ApprovalsTab
                loading={approvalsLoading}
                approvalWorkflowsQueryParams={approvalWorkflowsQueryParams}
                approvals={approvalsList}
                campaignId={campaign.id}
                isFetchingNextPage={isFetchingNextPageApprovals}
                hasNext={!!hasNextPageApprovals}
                loadMoreApprovals={fetchNextPageApprovals}
                refetchApprovalsData={refetchApprovalsData}
              />
            </TabPane>
            <TabPane
              tab={
                <div className="tasks-tab-pane">
                  Tasks{' '}
                  <span
                    className={classNames(
                      'count',
                      tasksCount > 9 && 'count_big'
                    )}
                  >
                    {tasksCount}
                  </span>{' '}
                </div>
              }
              key="tasks"
            >
              <div
                ref={tasksScrollContainer}
                className="collapse-container"
                onScroll={onScroll}
              >
                <TasksTab campaign={campaign} boardsLoading={boardsLoading} />
              </div>
            </TabPane>
            <TabPane
              tab={
                <>
                  Shared links{' '}
                  <span
                    className={classNames(
                      'count',
                      assetReviewsCount > 9 && 'count_big'
                    )}
                  >
                    {assetReviewsCount}
                  </span>{' '}
                </>
              }
              key="sharedLinks"
            >
              <SharedLinksTab
                loading={assetReviewsLoading}
                assetReviewsData={
                  assetReviewsData?.pages.flatMap((page) => page.edges) || []
                }
                isFetchingNextPage={isFetchingNextPageAssetReviews}
                hasNext={!!hasNextPageAssetReviews}
                fetchNextPage={fetchNextPageAssetReviews}
              />
            </TabPane>
          </Tabs>
        </div>
      </div>
      <CampaignTimeline
        campaignId={campaign.id || ''}
        visible={openTimeline}
        setVisible={setOpenTimeline}
      />
    </>
  );
}

function CampaignItem() {
  const { campaignId } = useParams();
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  const campaignQuery = useCampaignQuery(
    { campaignId: campaignId || '' },
    { enabled: !!campaignId }
  );
  const campaign = campaignQuery?.data;
  const campaignError = campaignQuery?.error;
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAxiosError(campaignError)) return;
    const status = campaignError?.response?.status;
    if (status !== 403 && status !== 404) return;
    message.error('This project is no longer available.');
    navigate('../');
  }, [campaignError, navigate]);

  useEffect(() => {
    if (campaign && campaign.workspaceId !== currentWorkspace?.id) {
      setCurrentWorkspace(campaign.workspaceId);
    }
  }, [campaign, currentWorkspace, setCurrentWorkspace]);
  if (!campaign || !currentWorkspace) return null;
  if (campaign.workspaceId !== currentWorkspace.id) return null;
  return <CampaignItemInWorkspace key={campaign.id} campaign={campaign} />;
}

export default CampaignItem;
